import {
  Box,
  Heading,
  Text,
  VStack,
  Link,
  Flex,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react';
import { Icon } from 'src/components/atoms/Icon';

export const DPAPage = () => {
  return (
    <Box
      bg="white"
      color="gray.800"
      maxW="756px"
      mx="auto"
      px={6}
      pt={16}
      pb={24}
      fontFamily="marketingPage"
      fontSize={'15px'}
      lineHeight={'24px'}>
      <VStack align="start" spacing={16}>
        <Box>
          <Heading as="h1" fontSize={'40px'} mb={4} fontFamily="marketingPage">
            Data Processing Agreement (DPA)
          </Heading>
          <Text fontSize="sm" color="gray.600">
            Last updated: September 2025
          </Text>
          <Text mt={4}>
            This Data Processing Agreement (&quot;DPA&quot;) supplements and
            forms part of the Terms of Service between Merkatus Oy
            (&quot;Processor&quot;) and the customer (&quot;Controller&quot;).
            In the event of a conflict, the provisions of this DPA shall prevail
            with respect to personal data processing.
          </Text>
          <Text mt={4}>
            This DPA is entered into in accordance with Article 28 of Regulation
            (EU) 2016/679 (General Data Protection Regulation,
            &quot;GDPR&quot;).
          </Text>
        </Box>

        {/* Section 1 */}
        <Box>
          <Heading as="h2" fontSize={'32px'} mb={4} fontFamily="marketingPage">
            1. Parties
          </Heading>
          <VStack align="start" spacing={3}>
            <Text>
              <Text as="span" fontWeight="semibold">
                Data Controller:
              </Text>{' '}
              Customer
            </Text>
            <Text>
              <Text as="span" fontWeight="semibold">
                Data Processor:
              </Text>{' '}
              Merkatus.io Oy
            </Text>
          </VStack>
        </Box>

        {/* Section 2 */}
        <Box>
          <Heading as="h2" fontSize={'32px'} mb={4} fontFamily="marketingPage">
            2. Background and Purpose
          </Heading>
          <VStack align="start" spacing={4}>
            <Text>
              The parties have entered into an agreement under which the Data
              Controller (
              <Text as="span" fontWeight="semibold">
                Controller
              </Text>
              ) has commissioned the Data Processor (
              <Text as="span" fontWeight="semibold">
                Processor
              </Text>
              ) to process personal data on behalf of the Controller (
              <Text as="span" fontWeight="semibold">
                Main Agreement
              </Text>
              ).
            </Text>
            <Text>
              These terms together with Annexes and the Main Agreement
              constitute the written agreement for the processing of such
              personal data as required by Article 28 of the EU General Data
              Protection Regulation (2016/679;
              <Text as="span" fontWeight="semibold">
                {' '}
                GDPR
              </Text>
              ).
            </Text>
            <Text>
              The subject matter, duration, nature, purpose, types of data
              categories to be processed, and the categories of data subjects
              whose information concerns are further specified in the Main
              Agreement and in the Annex of these terms. Unless otherwise
              agreed, each party shall bear its own costs related to the
              performance of its obligations under these terms.
            </Text>
          </VStack>
        </Box>

        {/* Section 3 */}
        <Box>
          <Heading as="h2" fontSize={'32px'} mb={4} fontFamily="marketingPage">
            3. General Responsibilities of Data Processor
          </Heading>
          <VStack align="start" spacing={4}>
            <Text>
              Processor shall ensure that the processing of personal data is
              performed in accordance with the GDPR and other mandatory
              legislation, and as reasonably instructed by Controller in the
              Main Agreement or otherwise in writing.
            </Text>
            <Text>
              If Processor is under an obligation to disclose or divulge
              personal data to a third party, Processor shall aim to notify
              Controller in advance, unless otherwise required by law.
            </Text>
            <Text>
              Processor shall reasonably assist Controller in exercising the
              data subject&apos;s rights under the GDPR in relation to
              Controller by providing information necessary to respond to an
              access request.
            </Text>
            <Text>
              Processor may charge for the same in accordance with its price
              list.
            </Text>
            <Text>
              Processor shall, at the request of Controller, provide Controller
              with information reasonably necessary to ensure that Processor has
              complied with these terms, the Main Agreement, and the law
              applicable to the processing measures. In the situations required
              by the GDPR, Processor shall also allow Controller, or an auditor
              appointed by Controller, to execute an audit or inspection that is
              reasonably necessary to ensure the above and/or assist to a
              reasonable extent in conducting a data protection impact
              assessment. Processor may charge for such measures in accordance
              with its price list. Processor shall aim to inform Controller if
              it considers that the instructions it has received are in breach
              of applicable law.
            </Text>
          </VStack>
        </Box>

        {/* Section 4 */}
        <Box>
          <Heading as="h2" fontSize={'32px'} mb={4} fontFamily="marketingPage">
            4. Subprocessors and Transfer Outside EU/EEA
          </Heading>
          <VStack align="start" spacing={4}>
            <Text>
              Processor will not transfer data outside the EU/EEA without
              Controller&apos;s written permission.
            </Text>
            <Text>
              Where Processor transfers personal data outside the EU/EEA in
              accordance with the above, Processor shall, upon request, provide
              Controller with a list of all processing locations outside the
              EU/EEA.
            </Text>
            <Text>
              If Processor transfers personal data outside the EU/EEA as
              described above, Processor must ensure that the data outside the
              EU/EEA is protected by a transfer mechanism in accordance with the
              GDPR, such as the model contractual clauses adopted by the
              European Commission. The applicable transfer mechanism shall be
              indicated in the list of processors referred to below.
            </Text>
            <Text>
              Controller provides general authorisation for Processor to engage
              sub-processors, subject to the conditions in this clause.
            </Text>
            <Text>A list of current sub-processors is set out in Annex 1.</Text>
            <Text>
              Processor shall give at least 30 days&apos; prior written notice
              of any intended addition or replacement of sub-processors, thereby
              giving Controller the opportunity to object to such changes for
              legitimate reasons.
            </Text>
            <Text>
              If Controller objects to a new sub-processor, Controller shall
              terminate the Main Agreement by providing written notice, with
              termination effective before the new sub-processor is engaged. No
              fees shall be refundable if Customer terminates pursuant to this
              clause.
            </Text>
            <Text>
              When using subprocessors, Processor shall enter into written
              agreements ensuring such subprocessors comply with data protection
              obligations materially equivalent to those binding Processor
              hereunder.
            </Text>
          </VStack>
        </Box>

        {/* Section 5 */}
        <Box>
          <Heading as="h2" fontSize={'32px'} mb={4} fontFamily="marketingPage">
            5. Data Protection and Data Breaches
          </Heading>
          <VStack align="start" spacing={4}>
            <Text>
              The personal data shall be considered confidential information of
              Controller, which Processor is obliged to keep confidential,
              unless otherwise provided for in the subject matter or the purpose
              of the contract.
            </Text>
            <Text>
              Processor declares that its employees and other representatives
              involved in the processing measures referred herein have either
              signed a non-disclosure agreement or are directly bound by law to
              observe professional secrecy.
            </Text>
            <Text>
              Processor shall ensure that the processing and storage of personal
              data is conducted in accordance with technical and organisational
              security measures that are appropriate and adequate considering
              the nature of the processing and the data. Upon assessing the
              adequate level of security, Processor shall take into account the
              state of the art, the costs, the scope and nature of the personal
              data processed, the risks associated with the processing measures,
              and similar factors. At the request of Controller, Processor shall
              provide Controller with a reasonable written explanation of its
              security measures and the adequacy of those measures, taking into
              account the established practices in the sector. If Processor
              considers that it does not have sufficient expertise to assess the
              adequate level of data protection in Processor&apos;s industry, it
              shall inform Controller. The applicable data security requirements
              may also be further specified in the Main Agreement, an Annex to
              these terms or any other binding document between the parties.
            </Text>
            <Text>
              If Processor has a reason to suspect that its premises or
              information systems have been breached or that personal data have
              otherwise been disclosed to a third party (other than as part of
              transfers permitted under the Main Agreement or these terms) or
              have been lost/altered, it must notify Controller. Processor shall
              assist Controller with necessary measures to comply with
              obligations applicable to the situation, including the drafting of
              notifications to Controller and the authorities, and otherwise
              cooperate with Controller.
            </Text>
          </VStack>
        </Box>

        {/* Section 6 */}
        <Box>
          <Heading as="h2" fontSize={'32px'} mb={4} fontFamily="marketingPage">
            6. Termination of Agreement
          </Heading>
          <Text>
            These terms are in force during the term of the Main Agreement.
          </Text>
          <Text>
            Upon the termination of the Main Agreement, Processor shall
            terminate the processing of personal data and return to Controller
            the data held by Processor that it is responsible for and destroy
            any remaining copies of the data unless otherwise provided by law.
          </Text>
          <Text>
            Other termination procedures may also be agreed upon a case-by-case
            basis. If the parties have agreed upon an obligation to preserve
            data or an obligation to assist in the termination or similar,
            Processor may only destroy the data after having successfully
            complied with all such requirements.
          </Text>
        </Box>

        {/* Section 7 */}
        <Box w="100%">
          <Heading as="h2" fontSize={'32px'} mb={4} fontFamily="marketingPage">
            Description of the personal data subject to processing and the data
            subjects
          </Heading>
          <Box borderWidth="1px" borderRadius="lg" p={6} w="100%">
            <VStack align="start" spacing={4}>
              <Box>
                <Text fontWeight="semibold">Purpose of processing:</Text>
                <Text>
                  Processor processes personal data as part of providing
                  cloud-based marketing intelligence software-as-a-service
                  together with related offerings to Controller as set out in
                  more detail in the Main Agreement.
                </Text>
              </Box>
              <Box>
                <Text fontWeight="semibold">Personal data types:</Text>
                <Text>
                  Within the scope of the services, Processor may process the
                  following personal data types:
                </Text>
                <UnorderedList pl={4} spacing={1}>
                  <ListItem>Name</ListItem>
                  <ListItem>Job title</ListItem>
                  <ListItem>Address</ListItem>
                  <ListItem>Email</ListItem>
                  <ListItem>Telephone number</ListItem>
                  <ListItem>Customer account data</ListItem>
                  <ListItem>Usage logs</ListItem>
                  <ListItem>
                    Any marketing or media data provided by Controller through
                    the service
                  </ListItem>
                </UnorderedList>
              </Box>
              <Box>
                <Text fontWeight="semibold">Data subject categories:</Text>
                <Text>
                  Within the scope of the services, personal data of the
                  following data subject categories may be processed:
                </Text>
                <UnorderedList pl={4} spacing={1}>
                  <ListItem>Customer&apos;s employees and personnel</ListItem>
                  <ListItem>
                    Customer&apos;s business contacts and leads
                  </ListItem>
                  <ListItem>
                    Recipients of Customer&apos;s marketing campaigns and
                    communications
                  </ListItem>
                  <ListItem>
                    Other individuals whose personal data is processed by
                    Processor on behalf of Customer for marketing intelligence,
                    planning, and decision-making purposes
                  </ListItem>
                </UnorderedList>
              </Box>
              <Box>
                <Text>
                  No special categories of personal data are intended to be
                  processed.
                </Text>
              </Box>
              <Box>
                <Text fontWeight="semibold">Duration of processing:</Text>
                <Text>For the duration of the Main Agreement.</Text>
              </Box>
            </VStack>
          </Box>
        </Box>

        {/* Section 8 */}
        <Box w="100%">
          <Heading as="h2" fontSize={'32px'} mb={4} fontFamily="marketingPage">
            Sub-processors
          </Heading>
          <Box borderWidth="1px" borderRadius="lg" p={6} w="100%">
            <VStack align="start" spacing={5}>
              <Box>
                <Text fontWeight="semibold">Name:</Text>
                <Text>AWS</Text>
                <Text fontWeight="semibold" mt={2}>
                  Description of processing:
                </Text>
                <Text>Service infrastructure provider</Text>
                <Text fontWeight="semibold" mt={2}>
                  Location of processing:
                </Text>
                <Text>EU/ETA</Text>
                <Text fontWeight="semibold" mt={2}>
                  Transfer mechanism if outside EU/ETA:
                </Text>
                <Text>N/A</Text>
              </Box>
              <Box>
                <Text fontWeight="semibold">Name:</Text>
                <Text>Amplitude</Text>
                <Text fontWeight="semibold" mt={2}>
                  Description of processing:
                </Text>
                <Text>
                  Registers statistical data on users&apos; behaviour on the
                  website. Used for internal analytics by the website operator.
                </Text>
                <Text fontWeight="semibold" mt={2}>
                  Location of processing:
                </Text>
                <Text>EU/ETA</Text>
                <Text fontWeight="semibold" mt={2}>
                  Transfer mechanism if outside EU/ETA:
                </Text>
                <Text>N/A</Text>
              </Box>
              <Box>
                <Text fontWeight="semibold">Name:</Text>
                <Text>Sentry</Text>
                <Text fontWeight="semibold" mt={2}>
                  Description of processing:
                </Text>
                <Text>
                  Registers data on visitors&apos; website behaviour. This is
                  used for internal analysis and website optimization.
                </Text>
                <Text fontWeight="semibold" mt={2}>
                  Location of processing:
                </Text>
                <Text>EU/ETA</Text>
                <Text fontWeight="semibold" mt={2}>
                  Transfer mechanism if outside EU/ETA:
                </Text>
                <Text>N/A</Text>
              </Box>
              <Text>
                The list of sub-processors is available at{' '}
                <Link
                  href="https://merkatus.io/subprocessors"
                  color="darkBlue.900"
                  isExternal
                  textDecoration="underline">
                  https://merkatus.io/subprocessors
                </Link>
                .
              </Text>
            </VStack>
          </Box>
        </Box>

        {/* Contact Section */}
        <Box>
          <Heading as="h2" fontSize={'32px'} mb={4} fontFamily="marketingPage">
            Contact
          </Heading>
          <Text>
            For any questions regarding this Data Processing Agreement:
            <br />
            <Flex my={1}>
              <Icon
                iconName="mail"
                width={26}
                height={26}
                fill={'transparent'}
                mr={1}
              />
              <Link
                color="darkBlue.900"
                href="mailto:support@merkatus.io"
                fontWeight={'semibold'}>
                support@merkatus.io
              </Link>
            </Flex>
            <Flex mb={1}>
              <Icon
                iconName="location"
                width={26}
                height={26}
                fill={'transparent'}
                mr={1}
              />
              Merkatus.io Oy, Helsinki, Finland
            </Flex>
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};
